import { lazy, Suspense, type ReactNode } from "react";
import { useRoute } from "../contexts/RouterContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { Sidebar } from "./Sidebar";
import { LiveView } from "../views/LiveView";

// Live is the kiosk's home view and stays in the main chunk; everything else
// is loaded on first navigation so the analysis/settings code (uPlot, the
// 1.2k-line settings editor) doesn't gate first paint.
const SessionsView = lazy(() => import("../views/SessionsView").then((m) => ({ default: m.SessionsView })));
const SettingsView = lazy(() => import("../views/SettingsView").then((m) => ({ default: m.SettingsView })));
const PitBoardView = lazy(() => import("./PitBoardView").then((m) => ({ default: m.PitBoardView })));

function ViewFallback() {
  return <div className="view-loading" aria-busy="true" />;
}
import type { FiredAlert } from "../types/alerts";
import type { AppConfig } from "../types/config";
import type { useSession } from "../hooks/useSession";
import type { StintClock } from "../hooks/useStintClock";
import type { Driver } from "../types/session";

interface ShellProps {
  statusBar: ReactNode;
  map: ReactNode;
  gauges: ReactNode;
  radioDrawer?: ReactNode;
  cameraPanel?: ReactNode;
  alertStrip?: ReactNode;
  alertDrawer?: ReactNode;
  connectionBanner?: ReactNode;
  /** Shown inside the pit board too, which bypasses the shell chrome. */
  simBanner?: ReactNode;
  pitBoardAlerts: FiredAlert[];
  pitBoardSessionId: number | null;
  pitBoardStint: StintClock;
  pitBoardDriverName: string | null;
  sessionHook: ReturnType<typeof useSession>;
  drivers: Driver[];
  config: AppConfig;
  onSaveConfig: (config: AppConfig) => Promise<{ ok: boolean; error?: string }>;
  onResetConfig: () => Promise<{ ok: boolean; error?: string; config?: AppConfig }>;
  onCreateDriver: (name: string) => Promise<{ ok: boolean; error?: string }>;
  onDeleteDriver: (id: number) => Promise<{ ok: boolean; error?: string }>;
}

export function Shell({
  statusBar,
  map,
  gauges,
  radioDrawer,
  cameraPanel,
  alertStrip,
  alertDrawer,
  connectionBanner,
  simBanner,
  pitBoardAlerts,
  pitBoardSessionId,
  pitBoardStint,
  pitBoardDriverName,
  sessionHook,
  drivers,
  config,
  onSaveConfig,
  onResetConfig,
  onCreateDriver,
  onDeleteDriver,
}: ShellProps) {
  const { route, navigate } = useRoute();

  if (route.view === "pitboard") {
    return (
      <ErrorBoundary name="Pit board" fullScreen>
        <Suspense fallback={<ViewFallback />}>
          <PitBoardView
            alerts={pitBoardAlerts}
            sessionId={pitBoardSessionId}
            stint={pitBoardStint}
            driverName={pitBoardDriverName}
            banner={simBanner}
            onExit={() => navigate({ view: "live" })}
          />
        </Suspense>
      </ErrorBoundary>
    );
  }

  const renderView = () => {
    switch (route.view) {
      case "sessions":
        return (
          <SessionsView
            sessionHook={sessionHook}
            drivers={drivers}
            gauges={config.gauges}
            topicPrefix={config.mqtt.topicPrefix}
          />
        );
      case "settings":
        return (
          <SettingsView
            config={config}
            onSave={onSaveConfig}
            onReset={onResetConfig}
            drivers={drivers}
            onCreateDriver={onCreateDriver}
            onDeleteDriver={onDeleteDriver}
          />
        );
      default:
        return (
          <LiveView
            statusBar={statusBar}
            map={map}
            gauges={gauges}
            radioDrawer={radioDrawer}
            cameraPanel={cameraPanel}
            alertStrip={alertStrip}
            alertDrawer={alertDrawer}
            connectionBanner={connectionBanner}
            sessions={sessionHook.sessions}
            activeSessionId={sessionHook.activeSession?.id ?? null}
          />
        );
    }
  };

  return (
    <div className="shell">
      <div className="shell-sidebar">
        <Sidebar />
      </div>
      <div className="shell-content">
        {route.view !== "live" && (
          <>
            <header className="shell-header">{statusBar}</header>
            {connectionBanner}
            {alertStrip}
            {alertDrawer}
          </>
        )}
        {/* Live renders its own <main>; other views get one here. */}
        {route.view === "live" ? (
          <div className="shell-view">
            <ErrorBoundary name="View">
              <Suspense fallback={<ViewFallback />}>{renderView()}</Suspense>
            </ErrorBoundary>
          </div>
        ) : (
          <main className="shell-view">
            <ErrorBoundary name="View">
              <Suspense fallback={<ViewFallback />}>{renderView()}</Suspense>
            </ErrorBoundary>
          </main>
        )}
      </div>
    </div>
  );
}
